import {
  Button,
  Heading,
  ButtonGroup,
  VStack,
  useToast,
  HStack,
  Text,
  Box,
  SimpleGrid,
  Flex,
} from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import axios from 'axios';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';

import { DatePicker } from '../../../../../components/Form/DatePicker';
import { MaskedInput } from '../../../../../components/Form/MaskedInput';
import { ReactMultiSelect } from '../../../../../components/Form/ReactMultiSelect';
import { SelectOption } from '../../../../../components/Form/ReactSelect';
import { IActivityItemBase } from '../../../../../models/activities';
import { translateError } from '../../../../../utils/errors';
import { InternationalPhoneInput } from '../../../../../components/Form/InternationalPhoneInput';
import { createExternalLinkBookingsService } from '../../../../../services/Bookings/CreateExternalLinkBookingsService';
import { listExternalLinkActivitiesSchedulesService } from '../../../../../services/Activities/ListExternalLinkActivitySchedulesService';
import {
  IShowExternalLinkActivitySchedule,
  showExternalLinkActivitySchedulesService,
} from '../../../../../services/Activities/ShowExternalLinkActivitySchedulesService';
import { validateCpf } from '../../../../../utils/documentValidation';

type NewBookingFormData = {
  bookedDate: Date;
  cpf?: string;
  description?: string;
  email: string;
  endTime?: string;
  itemsId?: string[];
  name: string;
  phone?: string;
  startTime?: string;
};

export interface INewBookingData extends NewBookingFormData {
  items?: IActivityItemBase[];
}

const registerBookingFormSchema = Yup.object().shape({
  bookedDate: Yup.date()
    .required('Requerido')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  cpf: Yup.string()
    .length(11, 'Valor inválido, requerido 11 dígitos')
    .test('is-valid', 'CPF inválido', (value) => validateCpf(value))
    .nullable()
    .transform((_, originalValue) =>
      originalValue.replace(/\D/g, '').length
        ? originalValue.replace(/\D/g, '')
        : null,
    ),
  description: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  email: Yup.string()
    .email('E-mail inválido')
    .required('E-mail requerido')
    .transform((value) => value.toLowerCase()),
  endTime: Yup.string().when('startTime', {
    is: (val?: string) => !!val,
    then: Yup.string()
      .matches(/^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/, 'Horário inválido')
      .nullable()
      .test(
        'is-greater',
        'Horário final deve ser maior que o inicial',
        function isSameOrAfter(value) {
          return moment(value, 'HH:mm').isSameOrAfter(
            moment(this.parent.startTime, 'HH:mm'),
          );
        },
      )
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      ),
    otherwise: Yup.string()
      .matches(/^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/, 'Horário inválido')
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      ),
  }),
  itemsId: Yup.array()
    .nullable()
    .transform((value, originalValue) =>
      originalValue.map((val: SelectOption) => val.value),
    ),
  name: Yup.string().required('Nome requerido'),
  phone: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  startTime: Yup.string()
    .matches(/^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/, 'Horário inválido')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
});

export const BookingRegister = (): JSX.Element => {
  const toast = useToast();

  const { search } = useLocation();

  const activityScheduleId = useMemo(
    () => qs.parse(search).scheduleId?.toString(),
    [search],
  );

  const [activitySchedule, setActivitySchedule] =
    useState<IShowExternalLinkActivitySchedule>();
  const [includeDates, setIncludeDates] = useState<Date[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [itemsSelectOptions, setItemsSelectOptions] = useState<SelectOption[]>(
    [],
  );

  const { register, formState, handleSubmit, control, reset } = useForm({
    resolver: yupResolver(registerBookingFormSchema),
  });

  const { errors } = formState;

  useEffect(() => {
    async function loadActivitySchedule(scheduleId: string): Promise<void> {
      const schedule = await showExternalLinkActivitySchedulesService(
        scheduleId,
      );

      setItemsSelectOptions(
        schedule.activity.items
          .filter((item) => item.isActive)
          .map((item) => ({
            label: item.name,
            value: item.id,
          })),
      );

      setActivitySchedule(schedule);
    }

    if (activityScheduleId) {
      loadActivitySchedule(activityScheduleId);
    }
  }, [activityScheduleId]);

  useEffect(() => {
    async function loadSchedules(activityId: string): Promise<void> {
      if (activityScheduleId) {
        try {
          const eventsSchedule =
            await listExternalLinkActivitiesSchedulesService({
              activityId,
              activityScheduleId,
              selectedMonth,
            });

          const scheduleMonthDaysAvailable = eventsSchedule.map((event) => {
            const [year, month, day] = event.date.split('-');

            return new Date(+year, +month - 1, +day);
          });

          setIncludeDates(scheduleMonthDaysAvailable);
        } catch (err) {
          if (axios.isAxiosError(err) && err.response?.status !== 401) {
            toast({
              title: 'Falha ao carregar dados',
              description:
                translateError({ message: err.response?.data.message }) ||
                'Ocorreu um erro ao carregar as datas disponíveis, tente novamente.',
              status: 'error',
              duration: 3000,
              isClosable: true,
              variant: 'subtle',
              position: 'top-right',
            });
          }
        }
      }
    }

    if (activitySchedule) {
      loadSchedules(activitySchedule.activityId);
    }
  }, [
    selectedYear,
    selectedMonth,
    toast,
    activityScheduleId,
    activitySchedule,
  ]);

  const handleBookingSubmit: SubmitHandler<NewBookingFormData> = useCallback(
    async ({
      bookedDate,
      cpf,
      description,
      email,
      endTime,
      itemsId,
      name,
      phone,
      startTime,
    }) => {
      if (activitySchedule) {
        const selectedItems = activitySchedule.activity.items.filter((item) =>
          itemsId?.includes(item.id),
        );

        const bookedDateString = bookedDate.toLocaleDateString('fr-CA', {
          timeZone: 'America/Sao_Paulo',
        });

        try {
          await createExternalLinkBookingsService({
            activityScheduleId: activitySchedule.id,
            bookedDate,
            cpf,
            email,
            description,
            endTime: new Date(`${bookedDateString}T${endTime}:00.000Z`),
            items: selectedItems,
            name,
            phone,
            startTime: new Date(`${bookedDateString}T${startTime}:00.000Z`),
          });

          toast({
            title: 'Cadastrado com sucesso',
            description: 'A reserva foi cadastrada corretamente.',
            status: 'success',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });

          reset({});
        } catch (err) {
          if (axios.isAxiosError(err) && err.response?.status !== 401) {
            if (err.response?.data.message === 'wallet-not-found') {
              toast({
                title: 'Carteira faltando',
                description:
                  'Membro não possui carteira cadastrada, confira o pagamento da reserva.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'top-right',
              });

              toast({
                title: 'Cadastrado com sucesso',
                description: 'A reserva foi cadastrada corretamente.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'top-right',
              });

              reset({});

              return;
            }

            if (err.response?.data.message === 'wallet-without-credits') {
              toast({
                title: 'Carteira sem crédito',
                description:
                  'Carteira sem créditos, confira o pagamento da reserva.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'top-right',
              });

              toast({
                title: 'Cadastrado com sucesso',
                description: 'A reserva foi cadastrada corretamente.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'top-right',
              });

              reset({});

              return;
            }

            toast({
              title: 'Falha no cadastro',
              description:
                translateError({ message: err.response?.data.message }) ||
                'Ocorreu um erro ao cadastrar a reserva, tente novamente.',
              status: 'error',
              duration: 3000,
              isClosable: true,
              variant: 'subtle',
              position: 'top-right',
            });
          }
        }
      }
    },
    [activitySchedule, reset, toast],
  );

  const handleMonthChange = useCallback((date: Date) => {
    setSelectedMonth(date.getMonth());
    setSelectedYear(date.getFullYear());
  }, []);

  return (
    <Flex minH="100vh" w="100%" my="6" px="6">
      <Box
        flex="1"
        borderRadius={8}
        bg="white"
        p="8"
        as="form"
        onSubmit={handleSubmit(handleBookingSubmit)}
      >
        <Heading size="lg" fontWeight="normal" mb="8">
          Cadastrar reserva
        </Heading>

        {!!activitySchedule?.modality && (
          <Heading size="md" fontWeight="normal" color="blue.500">
            <HStack>
              <Text>Modalidade:</Text>

              <Text>{activitySchedule.modality.title}</Text>
            </HStack>
          </Heading>
        )}

        <VStack mt="4" spacing="8">
          <MaskedInput
            label="Nome completo"
            error={errors.name}
            {...register('name')}
          />

          <SimpleGrid
            columns={
              activitySchedule?.allowPartialTimeBookings
                ? [1, 1, 2, 2, 3]
                : [1, 1, 2]
            }
            spacing="8"
            w="100%"
          >
            <MaskedInput
              label="E-mail"
              type="email"
              textTransform="lowercase"
              error={errors.email}
              {...register('email')}
            />

            <MaskedInput
              label="CPF"
              mask="cpf"
              error={errors.cpf}
              {...register('cpf')}
            />

            <InternationalPhoneInput
              label="Telefone"
              name="phone"
              control={control}
              error={errors.phone}
            />

            <DatePicker
              label="Data"
              includeDates={includeDates}
              isClearable={false}
              onMonthChange={handleMonthChange}
              control={control}
              error={errors.bookedDate}
              {...register('bookedDate')}
            />

            {!!activitySchedule?.allowPartialTimeBookings && (
              <>
                <MaskedInput
                  label="Horário início"
                  mask="time"
                  error={errors.startTime}
                  {...register('startTime')}
                />

                <MaskedInput
                  label="Horário fim"
                  mask="time"
                  error={errors.endTime}
                  {...register('endTime')}
                />
              </>
            )}
          </SimpleGrid>

          {!!activitySchedule?.activity?.items.length && (
            <ReactMultiSelect
              label="Itens"
              name="itemsId"
              options={itemsSelectOptions}
              control={control}
              error={errors.itemsId}
            />
          )}

          <MaskedInput
            label="Descrição"
            as="textarea"
            minHeight="160px"
            resize="none"
            py="2"
            error={errors.description}
            {...register('description')}
          />
        </VStack>

        <ButtonGroup mt="8" w="100%" justifyContent="flex-end">
          <Button
            colorScheme="green"
            type="submit"
            size="lg"
            isLoading={formState.isSubmitting}
          >
            Salvar
          </Button>
        </ButtonGroup>
      </Box>
    </Flex>
  );
};
